import { bindable, autoinject, computedFrom, Disposable } from "aurelia-framework";
import { Reflection } from "../../../utilities/reflection";
import { I18N } from "aurelia-i18n";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";

@autoinject
export class RangeSelector {

    @bindable
    public firstValue: number = undefined;

    @bindable
    public secondValue: number = undefined;

    @bindable
    public propertyName: string = "";

    @bindable
    public endLabel: string = "";

    @bindable
    public min: number = 0;

    @bindable
    public max: number = 100;

    @bindable
    public externalLabel: string;

    @bindable
    public mobileTitle: string;

    @bindable
    public mobileDescription: string;

    @bindable
    public showRangeValues: boolean = false;

    @bindable
    public showCommas: boolean = true;

    @bindable
    public isApplied: boolean = false;

    @bindable
    public step: number = 1;

    @bindable
    public setDefaultValues: boolean = true;

    @bindable
    public useNumberGrouping: boolean = true;

    @bindable
    public isMobile: boolean = false;
    
    public prevSecondValue: number;
    public prevFirstValue: number;

    public cleared: boolean = false;
    
    @bindable
    public closePopUpOnClear: boolean = true;
    
    constructor(private element: Element, private i18N: I18N, private eventAggregator: EventAggregator) { }

    bind()
    {
        if(!this.firstValue && this.setDefaultValues)
            this.firstValue = Number(this.min); //default to min value
        if(!this.secondValue && this.setDefaultValues)
            this.secondValue = Number(this.max); //default to max value
    }

    _subscriptionToDocumentClickEvent: Disposable;
    attached() {
        setTimeout(() => {
            this.isOpen = true;
            this.prevFirstValue = this.firstValue;
            this.prevSecondValue = this.secondValue;
            this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closePopup.bind(this));
        });
        this.updateExternalLabel();

        if (this.element) {
            $(this.element).on("mousedown", (e) => this.filterMouseHandler(e, 'down'));
            $(this.element).on("mouseup", (e) => this.filterMouseHandler(e, 'up'));
            $(this.element).on("click", (e) => { e.stopPropagation(); });
        }
    }

    detached() {
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();

        if(this.element)
        {
            $(this.element).off("mousedown");
            $(this.element).off("mouseup");
            $(this.element).off("click");
        }
    }

    isOpen:boolean;
    closePopup(x: any) {

        if(this.isMobile) return ;

        if (x && x.source == 'document' && this.isOpen && !this.mouseDownInFilter) {
            this.isOpen = false;
            this.applyFilters(true, true);
        }

        this.mouseDownInFilter = false;
    }

    mouseDownInFilter: boolean = false;

    filterMouseHandler($event, type: string) {
        if (type == 'down') {
            this.mouseDownInFilter = true;
        }
        else if (type == 'up') {
            //Fixed bug #https://rightship.atlassian.net/browse/PX-8349
            // if ($event)
            //     $event.stopPropagation();
            this.mouseDownInFilter = false;
        }
    }

    @computedFrom(Reflection.getPropertyPath<RangeSelector>(x => x.propertyName))
    public get label() {
        return this.i18N.tr('sliders-label-text').replace("{0}", this.propertyName ? this.propertyName : this.i18N.tr('value-text'));
    }

    @computedFrom(Reflection.getPropertyPath<RangeSelector>(x => x.secondValue), Reflection.getPropertyPath<RangeSelector>(x => x.firstValue))
    public get invalidSecondValue() {
        const secondValue: any = this.secondValue;
        return secondValue != null && secondValue != "" && this.secondValue < this.firstValue;
    }

    firstValueChanged(newValue, oldValue) {
        if (this.cleared && newValue != oldValue && newValue != undefined && oldValue != undefined) this.cleared = false;
        this.updateExternalLabel();
        const changeEvent: CustomEvent = new CustomEvent("reload-active-filters", { bubbles: true });
        this.element.dispatchEvent(changeEvent);
    }

    secondValueChanged(newValue, oldValue) {
        if (this.cleared && newValue != oldValue && newValue != undefined && oldValue != undefined) this.cleared = false;
        this.updateExternalLabel();
        const changeEvent: CustomEvent = new CustomEvent("reload-active-filters", { bubbles: true });
        this.element.dispatchEvent(changeEvent);
    }

    propertyNameChanged() {
        this.updateExternalLabel();
    }

    updateExternalLabel() {
        if ((this.firstValue || this.firstValue == 0) && (this.secondValue || this.secondValue == 0)) {
            this.externalLabel = this.isApplied ? `${this.propertyName} ${this.firstValue} - ${this.secondValue}` : `${this.propertyName}`;
            this.mobileTitle = this.propertyName;
            this.mobileDescription = this.cleared ? "" : `${this.firstValue} - ${this.secondValue}`;
        }
        else {
            this.externalLabel = "";
            this.mobileTitle = this.propertyName;
            this.mobileDescription = "";
        }
    }

    public clear() {
        this.isApplied = false;
        this.cleared = true;
        this.firstValue = undefined;
        this.secondValue = undefined;
        
        if(this.closePopUpOnClear) {
            setTimeout(() => {
                this.applyFilters(false);
            }, 120);
        }
       
        this.updateExternalLabel();
    }

    public reset() {
        this.firstValue = this.prevFirstValue;
        this.secondValue = this.prevSecondValue;
    }

    public applyFilters(closeFilter: boolean = true, reset: boolean = false) {
        if (this.invalidSecondValue) {
            return false;
        }
        if (reset) {
            this.reset();
        }

        //it will be applied only if it has not been cleared, or reset and was already applied when opened
        this.isApplied = this.cleared || (reset && !this.isApplied) ? false : true;
        this.updateExternalLabel();
        setTimeout(() => {
            const changeEvent: CustomEvent = new CustomEvent("apply-filters", { bubbles: true, detail: { closeFilter: closeFilter } });
            this.element.dispatchEvent(changeEvent);
        });
        // reset cleared after apply changes
        if (closeFilter && this.cleared)
            this.cleared = false;
    
        return true
        }
}

export class RangeSelectorHelper
{
    public static resetRangeFilter(low: number, high: number, defaults: { low: number, high: number })
    {
        const min = defaults && defaults.low? defaults.low: 0;
        const max = defaults && defaults.high? defaults.high: 100;
        return { low: CloneHelper.cloneObject(low? low: min), high: CloneHelper.cloneObject(high? high: max) };
    }
}
export class DateRangeSelectorHelper
{
    public static resetRangeFilter(from: Date, to: Date, defaults: { from: Date, to: Date })
    {
        const min = defaults && defaults.from? defaults.from: null;
        const max = defaults && defaults.to? defaults.to: null;
        return { from: CloneHelper.cloneObject(from? from: min), to: CloneHelper.cloneObject(to? to: max) };
    }
}